import { FormInputBlock } from './form-input-block.js'
import type { EventType, HtmlType, NameAndType } from '../../types.js'
import type { TsCodeType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { Expression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { TextOrExpression_assumeText } from '../../data/text-or-code.js'
import { BlockBindingType } from '../block.js'
import { Parameter } from '../../script/parameter.js'

export const InputBlockTypeNames = {
	text: 'Text',
	number: 'Number',
	textArea: 'Text Area',
} as const
export type InputBlockType = keyof typeof InputBlockTypeNames

@jsonClass('InputBlock')
export class InputBlock extends FormInputBlock {
	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 1,
		label: 'Label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
	})
	labelCode = new TextOrExpression_assumeExpression('', 'T')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 2,
		label: 'Binding type',
		options: Object.entries(BlockBindingType),
	})
	bindingType: BlockBindingType = 'binding'

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: (blocks) =>
			blocks[0].bindingType == 'binding' ? 'Binding' : 'Binding getter',
		codeLanguage: 'ts',
		tsReturnType: () => 'string | number',
		editorSize: 'singleline',
		isBinding: true,
		isMandatory: () => true,
	})
	bindingCode = new Expression()

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 3,
		label: 'Type',
		options: Object.entries(InputBlockTypeNames),
		isBinding: false,
	})
	type: InputBlockType = 'text'

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 4,
		label: 'Number of rows',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (block: InputBlock[]) => block[0].type == 'textArea',
	})
	numberOfRows = new Expression()

	@jsonProperty(Boolean)
	@ScreenEditorInput({
		inputType: 'checkbox',
		order: 4,
		label: 'Show clear button',
		isBinding: false,
	})
	showClearButton: boolean = false

	@jsonProperty(TextOrExpression_assumeText)
	@ScreenEditorInput({
		inputType: 'code',
		order: 5,
		label: 'Prefix',
		codeLanguage: 'ts',
		alternativeContentAreaName: 'prefix',
		tsReturnType: () => 'string',
		isVisible: (blocks: InputBlock[]) =>
			blocks[0].type == 'text' || blocks[0].type == 'number',
		editorSize: 'singleline',
		isBinding: false,
	})
	prefixCode = new TextOrExpression_assumeText('', 'T')

	@jsonProperty(TextOrExpression_assumeText)
	@ScreenEditorInput({
		inputType: 'code',
		order: 6,
		label: 'Suffix',
		codeLanguage: 'ts',
		alternativeContentAreaName: 'suffix',
		tsReturnType: () => 'string',
		isVisible: (blocks: InputBlock[]) =>
			blocks[0].type == 'text' || blocks[0].type == 'number',
		editorSize: 'singleline',
		isBinding: false,
	})
	suffixCode = new TextOrExpression_assumeText('', 'T')

	canAcceptVariableString = (variable: NameAndType) => true
	acceptVariableString = (variable: NameAndType) =>
		(this.bindingCode = new Expression(variable.name))

	constructor(init: Partial<InputBlock> = {}) {
		super()
		this.init(init)
	}

	get allowedEventTypes(): EventType[] {
		return [
			{
				name: 'keydown',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'KeyboardEvent',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'keyup',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'KeyboardEvent',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'ngModelChange',
				parameters: [
					new Parameter({
						name: 'value',
						type: 'any',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'click',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'MouseEvent',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'blur',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'FocusEvent',
						defaultExpression: '$event',
					}),
				],
			},
		]
	}

	getValidationValueType(): string {
		return this.type == 'number' ? 'number' : 'string'
	}

	producePreview(): HtmlType {
		return `<input tabindex="-1" value="${this.calculateExampleText()}">`
	}

	getEditorCategory(): string {
		return 'Core'
	}
}
